import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import isEmpty from 'lodash-es/isEmpty'

import { EMPTY_STRING } from '../../../constants/common'
import {
  NEWSBOARD_ENTITY,
  SEARCH_RESULTS_POSTS_ENTITY,
} from '../../../constants/entity'
import { FilterBar } from '../../Filter'
import { Page } from '../../Page'
import { PageHeader } from '../../PageHeader'
import { PostListContainer, PostListLayout, usePostListPage } from '../../Post'
import {
  getSearchFilterStateOptions,
  getSearchResultsBreadcrumbs,
} from '../SearchResults.utils'

import {
  QA_NAME_SEARCH_RESULTS_POSTS,
  SEARCH_QUERY_PARAM_KEY,
  SEARCH_RESULTS_POSTS_EXCLUDE_FILTERS,
  SEARCH_RESULTS_POSTS_FILTER_SUB_MENU,
} from './SearchResultsPosts.constants'
import {
  getSearchResultsPostsPermanentFilters,
  getShowEmptyListRules,
  parseSearchResultsPostsPermanentFilters,
} from './SearchResultsPosts.utils'

function SearchResultsPosts() {
  const [searchParams] = useSearchParams()

  const { t } = useTranslation()

  const searchQuery = searchParams.get(SEARCH_QUERY_PARAM_KEY) || EMPTY_STRING

  const asyncListOptions = {
    ...getSearchFilterStateOptions({
      entity: SEARCH_RESULTS_POSTS_ENTITY,
      searchQuery,
      sub: SEARCH_RESULTS_POSTS_FILTER_SUB_MENU,
    }),
    extraFilters: {
      ...getSearchResultsPostsPermanentFilters(),
      [SEARCH_QUERY_PARAM_KEY]: searchQuery,
    },
  }

  const listPage = usePostListPage({
    asyncListOptions,
    entity: SEARCH_RESULTS_POSTS_ENTITY,
    getPermanentFilters: getSearchResultsPostsPermanentFilters,
    pageTitle: t('SearchResults.PostsBrowserTabTitleKey'),
    sub: SEARCH_RESULTS_POSTS_FILTER_SUB_MENU,
  })

  const {
    availableFilters,
    filterValues,
    handleListConfigInit,
    handleSetFilterValues,
    isLoading,
    items,
    listLoading,
    ordering,
    pageScrollWrapperRef,
    updateListData,
  } = listPage

  useEffect(() => {
    handleListConfigInit()
  }, [searchQuery])

  const breadcrumbs = getSearchResultsBreadcrumbs({
    leadingBreadcrumbs: [
      {
        id: 'newsboard',
        title: t('BrowserTab.PostsPageTitleKey'),
        to: '/',
      },
    ],
    searchQuery,
    t,
  })

  const hasPosts = items?.length > 0

  const hideFiltersBar = !hasPosts && isEmpty(filterValues) && !listLoading

  return (
    <Page
      qaName={QA_NAME_SEARCH_RESULTS_POSTS}
      scrollWrapperRef={pageScrollWrapperRef}
    >
      <PostListContainer>
        <PageHeader breadcrumbs={breadcrumbs} isLoading={isLoading} />
        {availableFilters.length > 0 || listLoading ? (
          <FilterBar
            responsive
            availableFilters={availableFilters}
            canViewFilterTemplate={false}
            entity={NEWSBOARD_ENTITY}
            excludeFilters={SEARCH_RESULTS_POSTS_EXCLUDE_FILTERS}
            filterValues={filterValues}
            getPermanentFilters={getSearchResultsPostsPermanentFilters}
            isHidden={hideFiltersBar}
            isSkeleton={isLoading}
            ordering={ordering}
            parsePermanentFilters={parseSearchResultsPostsPermanentFilters}
            setFilterValues={handleSetFilterValues}
            shareBodyExtras={{ search_query: searchQuery }}
            shareEntity={SEARCH_RESULTS_POSTS_ENTITY}
            sub={SEARCH_RESULTS_POSTS_FILTER_SUB_MENU}
            onReload={updateListData}
          />
        ) : null}
        <PostListLayout
          getShowEmptyListRules={getShowEmptyListRules}
          listPage={listPage}
        />
      </PostListContainer>
    </Page>
  )
}

export default SearchResultsPosts
